import React, { useState, useMemo } from 'react';
import { useAppContext } from '../AppContext';
import useFirestoreQuery from '../hooks/useFirestoreQuery';
import NexusList from './NexusList';
import CreateNexusForm from './CreateNexusForm';
import CreateNexusPersonaForm from './CreateNexusPersonaForm';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from './LoadingSpinner';

function NexusPage() {
    const { t } = useTranslation();
    const { currentUserProfile, db, appId, query, collection, where, handlePageChange } = useAppContext();
    const [activeTab, setActiveTab] = useState('discover'); // discover, create, foundry

    const myNexusesQuery = useMemo(() => {
        if (!currentUserProfile?.id) return null;
        return query(collection(db, `artifacts/${appId}/public/data/nexuses`), where('memberIds', 'array-contains', currentUserProfile.id));
    }, [currentUserProfile, db, appId, query, collection, where]);

    const { data: myNexuses, isLoading } = useFirestoreQuery(myNexusesQuery);
    const activeNexus = myNexuses && myNexuses.length > 0 ? myNexuses[0] : null;
    const isOwner = activeNexus && activeNexus.ownerId === currentUserProfile?.id;

    if (isLoading) return <LoadingSpinner message="Locating your Nexus..." />;

    const enterNexus = () => {
        handlePageChange('nexus', { nexusId: activeNexus.id });
    };

    return (
        <div className="p-4 sm:p-6 bg-black/50 backdrop-blur-md rounded-2xl shadow-2xl ring-1 ring-white/5 max-w-6xl mx-auto text-white animate-fadeIn pb-24 relative overflow-hidden">
            {/* Ambient Background Elements */}
            <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900 via-black to-black mix-blend-screen" />
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10" />

            <Helmet>
                <title>Nexus Portal - Whispers of Harmony</title>
                <meta name="description" content="Discover and join Nexuses, specialized communities within Whispers of Harmony." />
            </Helmet>

            {activeNexus && activeTab === 'discover' ? (
                <div className="relative z-10 flex flex-col items-center justify-center py-12">
                    <h2 className="text-sm uppercase tracking-[0.3em] text-cyan-400 font-bold mb-2">Your Active Faction</h2>
                    <h1 className="text-5xl sm:text-6xl font-extrabold font-playfair tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-8 drop-shadow-[0_0_15px_rgba(236,72,153,0.5)] text-center">
                        {activeNexus.name}
                    </h1>
                    
                    <div className="mb-12 relative group cursor-pointer" onClick={enterNexus}>
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full blur opacity-40 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                        <button className="relative px-12 py-4 bg-black/80 ring-1 ring-white/20 rounded-full text-2xl font-bold text-white transition-all transform group-hover:scale-105 overflow-hidden">
                            <span className="relative z-10">Enter The Nexus</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                        </button>
                    </div>

                    <div className="flex gap-4 border-t border-white/10 pt-8 mt-8">
                        {isOwner && (
                            <button onClick={() => setActiveTab('foundry')} className="px-6 py-2 rounded-lg bg-indigo-900/40 hover:bg-indigo-800/60 ring-1 ring-indigo-500/50 transition-all font-semibold">
                                The Foundry
                            </button>
                        )}
                        <button onClick={() => setActiveTab('explore')} className="px-6 py-2 rounded-lg bg-gray-900/40 hover:bg-gray-800/60 ring-1 ring-white/10 transition-all text-gray-300 hover:text-white font-semibold">
                            Explore Other Nexuses
                        </button>
                    </div>
                </div>
            ) : (
                <div className="relative z-10">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-bold font-playfair text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">
                            {activeTab === 'explore' ? 'Other Nexuses' : activeTab === 'create' ? 'Found a Legacy' : activeTab === 'foundry' ? 'The Foundry' : 'Choose Your Faction'}
                        </h2>
                        <p className="text-gray-400 font-sans tracking-wide">
                            {activeTab === 'explore' ? 'Warning: You must leave your current Nexus before joining another.' : activeTab === 'discover' ? 'Pledge your allegiance to a Nexus to unlock Co-op games and clan features.' : ''}
                        </p>
                    </div>

                    <div className="flex justify-center border-b border-white/10 mb-8 gap-2 pb-4">
                        {(activeTab === 'explore' || activeTab === 'discover') && activeNexus && (
                            <button onClick={() => setActiveTab('discover')} className={`px-4 py-2 rounded-lg transition-all font-semibold ${activeTab === 'discover' ? 'bg-purple-600/50 text-white ring-1 ring-purple-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                                Return to Portal
                            </button>
                        )}
                        
                        {(!activeNexus || activeTab === 'explore') && (
                            <button onClick={() => setActiveTab(activeNexus ? 'explore' : 'discover')} className={`px-4 py-2 rounded-lg transition-all font-semibold ${(activeTab === 'discover' && !activeNexus) || activeTab === 'explore' ? 'bg-cyan-600/50 text-white ring-1 ring-cyan-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                                Browse Nexuses
                            </button>
                        )}

                        <button onClick={() => setActiveTab('create')} className={`px-4 py-2 rounded-lg transition-all font-semibold ${activeTab === 'create' ? 'bg-pink-600/50 text-white ring-1 ring-pink-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                            Found a Nexus
                        </button>
                        
                        {isOwner && (
                            <button onClick={() => setActiveTab('foundry')} className={`px-4 py-2 rounded-lg transition-all font-semibold ${activeTab === 'foundry' ? 'bg-indigo-600/50 text-white ring-1 ring-indigo-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                                The Foundry
                            </button>
                        )}
                    </div>

                    <div className="page-container mt-4">
                        {(activeTab === 'discover' || activeTab === 'explore') && <NexusList mode="discover" userNexusId={activeNexus?.id} />}
                        {activeTab === 'create' && <CreateNexusForm onNexusCreated={() => setActiveTab('discover')} />}
                        {activeTab === 'foundry' && isOwner && (
                            <div className="space-y-4 max-w-2xl mx-auto bg-black/40 p-6 rounded-xl ring-1 ring-white/5">
                                <h3 className="text-xl font-bold mb-4 text-indigo-300">Foundry Settings for {activeNexus.name}</h3>
                                <CreateNexusPersonaForm nexus={activeNexus} onPersonaCreated={() => { }} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NexusPage;